import { AppFunctionComponent, ResponsiveImage } from "../../types"
import React, { ReactNode, useContext, useEffect, useRef } from "react"
import styled, { css, ThemeContext } from "styled-components"
import Hero, {
  HeroDesktopLayout,
  HeroHeading,
  HeroImage,
  HeroImageBehavior,
  HeroMobileLayout,
  HeroText,
} from "../../components/common/sections/hero-section-refactored.component"
import { generateSources } from "../../helpers/images"
import {
  HeroBuilder,
  HeroBuilderDesktopLayout,
  HeroBuilderImageBehavior,
  HeroBuilderMobileLayout,
} from "../../types"
import { resolveImages } from "../responsive-image/responsive-image.resolver"
import RichText from "../rich-text/rich-text.resolver"
import { ScrollAnchor } from "../../components/common/sections/custom/innovation-lab-form/innovation-lab.component"

import VideoResolver from "../video/video.resolver"
import ReactPlayer from "react-player"
import useTranslationHook from "../../hooks/use-translation.hook"
import { Link } from "gatsby"

const StyledDisclaimer = styled.h3`
  font-family: Facto;
  font-size: 14px;
  font-weight: 400;
  line-height: 18.2px;
  text-align: left;
  color: #a0a0a0;
  grid-area: Disclaimer;
  text-align: center;
  margin-top: 30px;
  margin-bottom: 0;
`

const HeroContainer = styled(Hero)<{
  customStyle?: HeroBuilder["style"]
  hasDisclaimer?: boolean
  isMainPage?: boolean
}>`
  white-space: pre-wrap;

  ${({ customStyle }) =>
    customStyle?.noLeftTextPadding &&
    css`
      ${HeroText}, ${HeroHeading} {
        padding-left: 0;
      }
    `}

  ${({ customStyle }) =>
    `--text-color: ${customStyle?.landing?.textColor || "black"};`}
  ${({ customStyle }) =>
    `--heading-text-color: ${
      customStyle?.landing?.headingTextColor ||
      customStyle?.landing?.textColor ||
      "black"
    };`}
  ${({ customStyle }) =>
    customStyle &&
    css`
      --min-hero-height: ${customStyle.min_hero_height}px;
      --link-color: ${customStyle.landing?.linkColor ?? "inherit"};
      --text-color: ${customStyle.landing?.textColor || "inherit"};
      --button-color: ${customStyle.landing?.buttonColor ?? "inherit"};
      --button-border-color: ${customStyle.landing?.buttonBorderColor ??
      "inherit"};
      --header-main-text-font-size: ${customStyle.landing
        ?.headerMainTextFontSize ?? "inherit"};
      --header-text-font-size: ${customStyle.landing?.headerTextFontSize ??
      "1.2rem"};
      --header-text-font-weight: ${customStyle.landing?.headerFontWeight ??
      "inherit"};
      --mobile-text-align: ${customStyle.landing?.mobileTextAlign ?? "center"};
      --mobile-margin: ${customStyle.landing?.mobileMargin ?? "inherit"};
      --mobile-margin-top: ${customStyle.landing?.mobileMarginTop ?? "inherit"};
      --mobile-margin-top: ${customStyle.landing
        ?.mobileHeroHeaderPaddingBottom ?? "inherit"};
      --mobile-hero-padding: ${customStyle.landing
        ?.mobileHeroHeaderPaddingBottom ?? "30px"};
      --hero-background-color: ${customStyle.landing?.backgroundColor ??
      "inherit"};

      ${customStyle.landing?.width &&
      css`
        width: ${customStyle.landing?.width ?? "inherit"};
        max-width: ${customStyle.landing?.width ?? "inherit"};
      `}

      @media (min-width: ${({ theme }) =>
        theme.breakpoint.mobileHorizontal}px) {
        --text-max-width: ${customStyle.text_max_width}px;
        --heading-max-width: ${customStyle.heading_max_width}px;
      }
    `};

  ${({ hasDisclaimer }) =>
    hasDisclaimer &&
    css`
      section {
        ${StyledDisclaimer} {
          display: none;
        }
        @media (max-width: ${({ theme }) =>
            theme.breakpoint.tabletHorizontal}px) {
          grid-template-areas:
            "Image Image Image"
            "Disclaimer Disclaimer Disclaimer"
            "Heading Heading Heading"
            "Text Text Text";
          ${StyledDisclaimer} {
            display: block;
          }
        }
      }
    `}

  ${({ customStyle }) =>
    customStyle?.desktopImage?.height &&
    customStyle?.desktopImage?.width &&
    css`
      @media (min-width: ${({ theme }) => theme.breakpoint.desktop}px) {
        ${HeroImage} {
          height: ${customStyle.desktopImage.height};
          width: ${customStyle.desktopImage.width};
        }
      }
    `}
`

interface HeroBuilderProps extends HeroBuilder {
  submenu?: ReactNode
  isActive?: boolean
  hasDisclaimer?: boolean
}

const HeroResolver: AppFunctionComponent<HeroBuilderProps> = ({
  headingText,
  text,
  image,
  thumbnailImage,
  layoutDesktop,
  layoutMobile,
  imageBehavior,
  style,
  submenu,
  isActive,
  hasDisclaimer,
}) => {
  const theme = useContext(ThemeContext)
  const { translate } = useTranslationHook()

  const playerRef = useRef<ReactPlayer | null>(null)

  // Effect to handle unloading when the slide becomes inactive
  useEffect(() => {
    if (typeof isActive === "boolean" && !isActive && playerRef.current) {
      // Stop the video when the slide goes off-screen (inactive)
      playerRef.current.getInternalPlayer()?.pause()
      // Optionally, remove the video source to free up memory
      playerRef.current.getInternalPlayer().src = ""
    }
  }, [isActive])

  const resolveDesktopLayout = (
    layout: HeroBuilderDesktopLayout
  ): HeroDesktopLayout => {
    switch (layout) {
      case HeroBuilderDesktopLayout.SeparatedTexts:
        return HeroDesktopLayout.SeparatedTexts
      case HeroBuilderDesktopLayout.CenteredTexts:
        return HeroDesktopLayout.CenteredTexts
      case HeroBuilderDesktopLayout.SingleColumn:
        return HeroDesktopLayout.CenteredSingleColumn
      case HeroBuilderDesktopLayout.LandingPageWithFullWith:
        return HeroDesktopLayout.LandingPageWithFullWidth
      case HeroBuilderDesktopLayout.BlackSingleColumnFullWidth:
        return HeroDesktopLayout.BlackCenteredSingleColumnFullWidth
      case HeroBuilderDesktopLayout.HeadingInLineLayout:
        return HeroDesktopLayout.HeadingInLineLayout
      case HeroBuilderDesktopLayout.SeparatedTextsKompakt:
        return HeroDesktopLayout.SeparatedTextsKompakt
      case HeroBuilderDesktopLayout.ImageTextKompakt:
        return HeroDesktopLayout.ImageTextKompakt
      default:
        return HeroDesktopLayout.CenteredTexts
    }
  }

  const resolveMobileLayout = (
    layout: HeroBuilderMobileLayout
  ): HeroMobileLayout => {
    switch (layout) {
      case HeroBuilderMobileLayout.HeadingImageText:
        return HeroMobileLayout.HeadingImageText
      case HeroBuilderMobileLayout.Fit:
        return HeroMobileLayout.Fit
      case HeroBuilderMobileLayout.Fill:
        return HeroMobileLayout.Fill
      case HeroBuilderMobileLayout.Custom:
        return HeroMobileLayout.TextOnImage
      case HeroBuilderMobileLayout.SingleColumn:
        return HeroMobileLayout.CenteredSingleColumn
      case HeroBuilderMobileLayout.ImageHeadingText:
        return HeroMobileLayout.ImageHeadingText
      case HeroBuilderMobileLayout.TextOnImage:
        return HeroMobileLayout.TextOnImage
      default:
        return HeroMobileLayout.HeadingImageText
    }
  }

  const resolveImageBehavior = (
    layout: HeroBuilderImageBehavior
  ): HeroImageBehavior => {
    switch (layout) {
      case HeroBuilderImageBehavior.Fit:
        return HeroImageBehavior.Fit
      case HeroBuilderImageBehavior.Fill:
        return HeroImageBehavior.Fill
      case HeroBuilderImageBehavior.Full:
        return HeroImageBehavior.Full
      case HeroBuilderImageBehavior.Cover:
        return HeroImageBehavior.Cover
      case HeroBuilderImageBehavior.Contain:
        return HeroImageBehavior.Contain
      case HeroBuilderImageBehavior.OriginalSize:
        return HeroImageBehavior.OriginalSize
      default:
        return HeroImageBehavior.Fit
    }
  }

  const resolveAsset = (
    type?: string,
    image?: ResponsiveImage,
    thumbnailImage?: any,
    usePlaceholderVideoImage?: boolean
  ) => {
    if (!type || !image) return null

    if (type.includes("image")) {
      return (
        <HeroImage
          image={generateSources(resolveImages(image, theme))}
          // "eager" is recommended option for all images above the fold to avoid Cumulative Layout Shifts that are base score for Core Web Vitals
          loading="eager"
        />
      )
    } else if (type.includes("video") && typeof window !== "undefined") {
      return (
        <VideoResolver
          ref={playerRef}
          autoplay
          loop
          media={image.desktopImage}
          placeholder={
            usePlaceholderVideoImage && thumbnailImage?.gatsbyImageData
          }
          title="Something"
          height="100%"
          objectFit="cover"
        />
      )
    } else {
      return null
    }
  }

  return (
    <HeroContainer
      desktopLayout={resolveDesktopLayout(layoutDesktop)}
      mobileLayout={resolveMobileLayout(layoutMobile)}
      imageBehavior={resolveImageBehavior(imageBehavior)}
      customStyle={style}
      hasDisclaimer={hasDisclaimer}
      submenu={submenu}
    >
      <ScrollAnchor id="top" />
      {hasDisclaimer && (
        <StyledDisclaimer>{translate("kompakt-disclaimer")}</StyledDisclaimer>
      )}
      <HeroHeading>
        {headingText && <RichText content={headingText} />}
      </HeroHeading>
      <HeroText>{text && <RichText content={text} />}</HeroText>
      {image ? (
        image.linkUrl ? (
          <>
            <Link to={image.linkUrl}>
              {resolveAsset(
                image.desktopImage?.file.contentType,
                image,
                thumbnailImage,
                Boolean(style?.usePlaceholderVideoImage)
              )}
            </Link>
          </>
        ) : (
          <>
            {resolveAsset(
              image.desktopImage?.file.contentType,
              image,
              thumbnailImage,
              Boolean(style?.usePlaceholderVideoImage)
            )}
            {image.description &&
              layoutDesktop ===
                HeroBuilderDesktopLayout.BlackSingleColumnFullWidth && (
                <HeroText style={{ maxWidth: 600 }}>
                  {image.description}
                </HeroText>
              )}
          </>
        )
      ) : null}
    </HeroContainer>
  )
}

export default HeroResolver
